import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import React, { ElementType, FC, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { Grid, useTheme } from "@mui/material";
import { AlertColor } from "@mui/material/Alert/Alert";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import { useGetOrg } from "@src/api/organizations/get-organization";
import {
  dismissAlert,
  getIsAlertDismissed,
} from "@src/store/slices/appSettings";
import { theme } from "@src/theme";
import { openNewTab } from "@src/utils/openNewTab";

interface IgAlertSimpleProps {
  severity: AlertColor;
  message: ReactNode | string;
  alertKey: string;
  sx?: SxProps<Theme>;
  dismissId?: string;
  onDismiss?: () => void;
  link?: string;
}

const IconMap: Record<AlertColor, ElementType> = {
  error: CircleX,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

const ColorMap: Record<AlertColor, string> = {
  error: theme.igBorders.error.default,
  warning: theme.igBorders.warning.default,
  info: theme.igBorders.info.default,
  success: theme.igBorders.success.default,
};

// Since this component needs access to redux we can't add to ig-theme
export const IgAlertSimple: FC<IgAlertSimpleProps> = ({
  severity,
  message,
  alertKey,
  sx,
  dismissId,
  onDismiss,
  link,
}) => {
  const Icon: ElementType = IconMap[severity];
  const dispatch = useDispatch();
  const theme = useTheme();
  const org = useGetOrg();
  const _isDismissed = useSelector(
    getIsAlertDismissed(org.data?.org.id, dismissId)
  );
  const isDismissed = dismissId ? _isDismissed : false;

  if (isDismissed) {
    return null;
  }
  return (
    <Grid
      id={alertKey}
      direction={"row"}
      container
      item
      alignItems={"center"}
      flexGrow={8}
      sm
      sx={{
        minHeight: "50px",
        border: `1px solid ${ColorMap[severity]}`,
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "8px 15px 8px 15px",
        ...sx,
      }}
    >
      <Grid item mr={1} sx={{ height: "24px" }}>
        <Icon color={ColorMap[severity]} size={22} />
      </Grid>
      <Grid item sm>
        {typeof message === "string" ? (
          <IgTypography>{message}</IgTypography>
        ) : (
          <span>{message}</span>
        )}
      </Grid>
      {link && (
        <IgTypography
          igStyle={"linkNoBold"}
          sx={{
            marginLeft: "10px",
          }}
          onClick={() => {
            openNewTab(link);
          }}
        >
          Learn more
        </IgTypography>
      )}
      {dismissId && (
        <Grid item>
          <IgIconButton
            onClick={() => {
              dispatch(
                dismissAlert({ orgId: org.data.org.id, alertId: dismissId })
              );
              if (onDismiss) {
                onDismiss();
              }
            }}
            iconType={"clear"}
          />
        </Grid>
      )}
    </Grid>
  );
};
